import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  BarChart3,
  TrendingUp,
  Users,
  BookOpen,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Award,
  ClipboardList,
  Target,
  ArrowUp,
  ArrowDown
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsDashboard = () => {
  const { user } = useAuth();
  const [trainingData, setTrainingData] = useState(null);
  const [safetyData, setSafetyData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("training");

  useEffect(() => {
    fetchAllAnalytics();
  }, []);

  const fetchAllAnalytics = async () => {
    try {
      const [trainingRes, safetyRes, employeeRes] = await Promise.all([
        axios.get(`${API}/analytics/training-effectiveness`, { withCredentials: true }),
        axios.get(`${API}/analytics/safety-insights`, { withCredentials: true }),
        axios.get(`${API}/analytics/employee-performance`, { withCredentials: true })
      ]);
      setTrainingData(trainingRes.data);
      setSafetyData(safetyRes.data);
      setEmployeeData(employeeRes.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="analytics-dashboard">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
              Analytics Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Training effectiveness, safety insights, and employee performance metrics.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="training" data-testid="tab-training">Training</TabsTrigger>
            <TabsTrigger value="safety" data-testid="tab-safety">Safety</TabsTrigger>
            <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
          </TabsList>

          {/* Training Effectiveness Tab */}
          <TabsContent value="training" className="space-y-6">
            {trainingData && (
              <>
                {/* Overview Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="stat-card">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <BookOpen className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Total Assignments</p>
                          <p className="font-heading text-2xl font-bold text-primary">
                            {trainingData.overview.total_assignments}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="stat-card">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Completion Rate</p>
                          <p className="font-heading text-2xl font-bold text-emerald-600">
                            {trainingData.overview.completion_rate}%
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="stat-card">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Award className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Avg Quiz Score</p>
                          <p className="font-heading text-2xl font-bold text-amber-600">
                            {trainingData.overview.avg_quiz_score}%
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="stat-card">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                          <Clock className="w-5 h-5 text-violet-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Avg Completion</p>
                          <p className="font-heading text-2xl font-bold text-violet-600">
                            {trainingData.overview.avg_completion_days} days
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Category Breakdown & Trend */}
                <div className="grid md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="font-heading text-lg">Completion by Category</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {trainingData.category_breakdown.map((cat, i) => (
                        <div key={i}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="capitalize">{cat.category}</span>
                            <span className="font-medium">{cat.completion_rate}%</span>
                          </div>
                          <Progress value={cat.completion_rate} className="h-2" />
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="font-heading text-lg">Monthly Completions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-end justify-between h-40 gap-2">
                        {trainingData.monthly_trend.map((month, i) => {
                          const maxVal = Math.max(...trainingData.monthly_trend.map(m => m.completions)) || 1;
                          const height = (month.completions / maxVal) * 100;
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center gap-1">
                              <div 
                                className="w-full bg-accent rounded-t transition-all"
                                style={{ height: `${Math.max(height, 5)}%` }}
                              />
                              <span className="text-xs text-muted-foreground">{month.month.split(' ')[0]}</span>
                              <span className="text-xs font-medium">{month.completions}</span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Safety Insights Tab */}
          <TabsContent value="safety" className="space-y-6">
            {safetyData && (
              <>
                {/* Overview Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="stat-card">
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">Total Incidents</p>
                      <p className="font-heading text-2xl font-bold text-rose-600">
                        {safetyData.incident_analysis.total_incidents}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="stat-card">
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">Inspections</p>
                      <p className="font-heading text-2xl font-bold text-blue-600">
                        {safetyData.inspection_analysis.total_inspections}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="stat-card">
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">Inspection Pass Rate</p>
                      <p className="font-heading text-2xl font-bold text-emerald-600">
                        {safetyData.inspection_analysis.pass_rate}%
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="stat-card">
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">JHAs Completed</p>
                      <p className="font-heading text-2xl font-bold text-violet-600">
                        {safetyData.jha_analysis.total_jhas}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Incident & JHA Analysis */}
                <div className="grid md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-rose-500" />
                        Incidents by Severity
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.entries(safetyData.incident_analysis.by_severity).map(([level, count]) => (
                          <div key={level} className="flex items-center justify-between">
                            <Badge className={
                              level === 'critical' ? 'badge-danger' :
                              level === 'high' ? 'bg-orange-100 text-orange-700' :
                              level === 'medium' ? 'badge-warning' : 'badge-neutral'
                            }>
                              {level.charAt(0).toUpperCase() + level.slice(1)}
                            </Badge>
                            <span className="font-semibold">{count}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Target className="w-5 h-5 text-violet-500" />
                        JHA Risk Distribution
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.entries(safetyData.jha_analysis.by_risk_level).map(([level, count]) => (
                          <div key={level} className="flex items-center justify-between">
                            <Badge className={
                              level === 'Critical' ? 'badge-danger' :
                              level === 'High' ? 'bg-orange-100 text-orange-700' :
                              level === 'Medium' ? 'badge-warning' : 'badge-success'
                            }>
                              {level}
                            </Badge>
                            <span className="font-semibold">{count}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Incident Types */}
                {safetyData.incident_analysis.by_type.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="font-heading text-lg">Incident Types</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {safetyData.incident_analysis.by_type.map((item, i) => (
                          <Badge key={i} variant="outline" className="px-3 py-1">
                            {item.type}: <span className="font-bold ml-1">{item.count}</span>
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Employee Performance Tab */}
          <TabsContent value="employees" className="space-y-6">
            {employeeData && (
              <>
                {/* Department Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Department Performance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {employeeData.department_breakdown.map((dept, i) => (
                        <div key={i} className="p-4 rounded-lg border border-slate-200">
                          <h4 className="font-medium text-primary">{dept.department}</h4>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-sm text-muted-foreground">{dept.employees} employees</span>
                            <Badge className={dept.completion_rate >= 80 ? 'badge-success' : dept.completion_rate >= 50 ? 'badge-warning' : 'badge-danger'}>
                              {dept.completion_rate}%
                            </Badge>
                          </div>
                          <Progress value={dept.completion_rate} className="h-2 mt-2" />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Top Performers */}
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-500" />
                      Top Performers
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {employeeData.employee_rankings.slice(0, 10).map((emp, i) => (
                        <div key={emp.employee_id} className="flex items-center justify-between p-3 rounded-lg bg-secondary">
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                              i === 0 ? 'bg-amber-400 text-white' :
                              i === 1 ? 'bg-slate-400 text-white' :
                              i === 2 ? 'bg-amber-700 text-white' : 'bg-slate-200 text-slate-600'
                            }`}>
                              {i + 1}
                            </span>
                            <div>
                              <p className="font-medium">{emp.name}</p>
                              <p className="text-xs text-muted-foreground">{emp.department || 'No department'}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-emerald-600">{emp.completion_rate}%</p>
                            <p className="text-xs text-muted-foreground">{emp.completed}/{emp.total_assigned} completed</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsDashboard;
